import { useEffect, useState } from 'react';
import { extractYouTubeId, checkYouTubeEmbeddable } from './useYouTubePlayer';

const CACHE_KEY = 'maestro:embed-cache:v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h — YT rights change rarely

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch { return {}; }
}

function writeCache(entries) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(entries)); } catch { /* ignore */ }
}

/**
 * For a list of songs, resolve a map { [songId]: 'audio' | 'sync' | 'metronome' | 'unknown' }
 * describing what playback mode Maestro can offer:
 *   - audio     : song has audio_url → local file synced
 *   - sync      : YouTube oEmbed 200 → video embed likely works
 *   - metronome : no video, or oEmbed 404 (removed/private) → metronome mode only
 *   - unknown   : still checking
 *
 * Results are cached in sessionStorage for 24 h to avoid re-hitting oEmbed on every list refresh.
 * Bounded concurrency of 6 to stay polite with YouTube's public endpoint.
 */
export default function useSongEmbeddability(songs) {
  const [map, setMap] = useState({});

  useEffect(() => {
    if (!Array.isArray(songs) || songs.length === 0) { setMap({}); return; }
    let cancelled = false;
    const now = Date.now();
    const cache = readCache();
    const initial = {};
    const needCheck = [];

    for (const s of songs) {
      if (!s?.id) continue;
      if (s.audio_url) { initial[s.id] = 'audio'; continue; }
      const url = s.video_url_alt || s.video_url;
      const vid = extractYouTubeId(url);
      if (!vid) { initial[s.id] = 'metronome'; continue; }
      const entry = cache[vid];
      if (entry && (now - entry.at) < CACHE_TTL_MS) {
        initial[s.id] = entry.ok ? 'sync' : 'metronome';
      } else {
        initial[s.id] = 'unknown';
        needCheck.push({ songId: s.id, vid });
      }
    }
    setMap(initial);

    if (needCheck.length === 0) return;

    const CONCURRENCY = 6;
    let cursor = 0;
    const results = { ...initial };
    const updatedCache = { ...cache };

    const runOne = async () => {
      while (!cancelled && cursor < needCheck.length) {
        const i = cursor++;
        const { songId, vid } = needCheck[i];
        try {
          const ok = await checkYouTubeEmbeddable(vid);
          if (cancelled) return;
          results[songId] = ok ? 'sync' : 'metronome';
          updatedCache[vid] = { ok, at: Date.now() };
        } catch {
          results[songId] = 'metronome';
        }
        if (!cancelled) setMap({ ...results });
      }
    };

    Promise.all(Array.from({ length: CONCURRENCY }, runOne)).then(() => {
      if (!cancelled) writeCache(updatedCache);
    });

    return () => { cancelled = true; };
  }, [songs]);

  return map;
}
